<script lang="ts">
import {} from '@types/google.maps'
import Vue from 'vue'
import {Component, Prop} from 'vue-property-decorator'
import {Loader} from '@googlemaps/js-api-loader'
import {customMapStyles} from '@/utils/maps'

@Component({})
export default class SpaceDeviceMap extends Vue {
  // Home address in human readable format: 123 main street, Springfield, NY
  @Prop()
  homeAddress: string

  // Lat long in string format: 33.8425,-84.2151
  @Prop()
  deviceLatLng: string

  google = null
  error: string = null

  async mounted() {
    this.google = await new Loader({
      apiKey: window.BOOT.googleMapsApiKey,
      version: 'beta',
      libraries: ['maps', 'marker', 'places', 'geometry'],
    }).load()
    this.initMap()
  }

  initMap() {
    if (!this.deviceLatLng) {
      this.error = 'No location for device'
      return
    }

    const DEFAULT_ZOOM = 16
    const HOME_DISTANCE_TRESHOLD = 250 // meters
    const geocoder: google.maps.Geocoder = new this.google.maps.Geocoder()
    const deviceLatLng = this.parseLatLngFromString(this.deviceLatLng)

    const map: google.maps.Map = new this.google.maps.Map(this.$refs.map, {
      center: deviceLatLng,
      zoom: DEFAULT_ZOOM,
      styles: customMapStyles,
      zoomControl: true,
      fullscreenControl: true,
      mapTypeControl: false,
      scaleControl: false,
      streetViewControl: false,
      rotateControl: false,
      zoomControlOptions: {
        position: google.maps.ControlPosition.TOP_LEFT,
      },
    })

    // Use geocoding to convert home address to lat/lng
    geocoder.geocode({address: this.homeAddress}, (results, status) => {
      if (status === 'OK') {
        let distance = google.maps.geometry.spherical.computeDistanceBetween(
          results[0].geometry.location,
          deviceLatLng
        )
        let isHome = distance < HOME_DISTANCE_TRESHOLD

        let homeMarker: google.maps.Marker = new this.google.maps.Marker({
          position: results[0].geometry.location,
          map: map,
          title: 'Home',
          icon: isHome
            ? require('@/assets/icons/marker-home-active.svg')
            : require('@/assets/icons/marker-home.svg'),
        })

        // Add device address
        let deviceMarker: google.maps.Marker = new this.google.maps.Marker({
          position: deviceLatLng,
          map: isHome ? null : map,
          title: 'Device',
          icon: require('@/assets/icons/marker.svg'),
        })

        // Center and fit so they're both visible
        let bounds: google.maps.LatLngBounds = new this.google.maps.LatLngBounds()
        bounds.extend(deviceMarker.getPosition())
        bounds.extend(homeMarker.getPosition())
        map.setCenter(bounds.getCenter())
        map.fitBounds(bounds)

        // Make sure we are not too zoomed in.
        google.maps.event.addListenerOnce(map, 'idle', function () {
          if (map.getZoom() > DEFAULT_ZOOM) {
            map.setZoom(DEFAULT_ZOOM)
          }
        })
      } else {
        this.error = 'Address geocoding error: ' + status
      }
    })
  }

  parseLatLngFromString(str: string): google.maps.LatLng {
    let parts = str.split(',').map(parseFloat)
    return new this.google.maps.LatLng(parts[0], parts[1])
  }
}
</script>

<template>
  <div>
    <div v-if="error" class="bg-light px-4 py-6 text-center">
      <img src="@/assets/icons/marker.svg" height="25" />
      {{ error }}
    </div>
    <div v-else ref="map" style="height: 240px"></div>
  </div>
</template>

<style lang="scss" scoped>
@import '@/assets/css/_variables.scss';
</style>
